import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { PrModalComponent } from '@app/components/pr-modal/pr-modal/pr-modal.component';
import {
  CityRef,
  SwisslifeLocationService,
} from '@app/services/swisslife-location.service';

/**
 * "Je ne trouve pas ma ville / mon code postal" finder.
 *
 * Problem it solves: Swisslife enforces the official INSEE / La Poste postal
 * codes, which CGPs often do not know. The existing dropdowns only let them
 * filter cities *after* typing a zip code — a dead end when the zip is unknown.
 *
 * This component exposes a link that opens a modal where the user can search the
 * reference list by city NAME **or** zip code, and pick the right pair in one
 * click. The selected `{ city, zipCode }` is emitted so the host can patch its
 * form (both fields at once).
 */
@Component({
  selector: 'pr-city-finder',
  templateUrl: './pr-city-finder.component.html',
  styleUrls: ['./pr-city-finder.component.scss'],
  standalone: false,
})
export class PrCityFinderComponent {
  /** Reference city list (from `ReferenceDao.getCities()`). */
  @Input() cities: CityRef[] = [];
  /** Label of the trigger link. */
  @Input() linkLabel = 'Je ne trouve pas ma ville ou mon code postal';
  /** Modal title. */
  @Input() title = 'Rechercher une ville ou un code postal';

  /** Emits the official INSEE/La Poste pair chosen by the user. */
  @Output() citySelected = new EventEmitter<{
    city: string;
    zipCode: string;
  }>();

  @ViewChild('finderModal') finderModal: PrModalComponent;

  searchTerm = '';
  results: CityRef[] = [];

  constructor(private swisslifeLocation: SwisslifeLocationService) {}

  open() {
    this.searchTerm = '';
    this.results = this.swisslifeLocation.searchCities(this.cities, '');
    this.finderModal.open();
  }

  onSearch(term: string) {
    this.searchTerm = term;
    this.results = this.swisslifeLocation.searchCities(this.cities, term);
  }

  select(item: CityRef) {
    this.citySelected.emit({ city: item.city, zipCode: item.zipCode });
    this.finderModal.close();
  }

  close() {
    this.finderModal.close();
  }
}
